import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { LogoMark } from "./components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#FFB300";
const CREAM = "#EDEBE4";
const BG = "#050505";
const BG_ALT = "#0A0A0A";

const TOKEN_KEY = "cg_admin_token";

const monoStyle = {
  fontFamily: "'JetBrains Mono', monospace",
  letterSpacing: "0.2em",
  textTransform: "uppercase",
};

const fontImport = `@import url('https://fonts.googleapis.com/css2?family=Fraunces:wght@300;400;500&family=JetBrains+Mono:wght@300;400;500&family=Inter:wght@300;400;500&display=swap');`;

function LoginView({ onAuthed }) {
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/admin/login`, { password });
      localStorage.setItem(TOKEN_KEY, data.token);
      onAuthed(data.token);
    } catch (e2) {
      setErr(e2.response?.status === 401 ? "Invalid password." : "Login error.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      style={{
        minHeight: "100vh",
        background: BG,
        color: CREAM,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        fontFamily: "'Inter', sans-serif",
      }}
    >
      <style>{`${fontImport} body { background: ${BG}; margin: 0; }`}</style>
      <form
        onSubmit={submit}
        style={{
          width: "100%",
          maxWidth: 420,
          border: `1px solid ${GOLD}33`,
          background: BG_ALT,
          padding: "48px 36px",
        }}
      >
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 28 }}>
          <LogoMark mode="dark" size={56} />
        </div>
        <div style={{ ...monoStyle, fontSize: 10, color: GOLD, textAlign: "center", marginBottom: 12 }}>
          — Admin
        </div>
        <h1
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 34,
            fontWeight: 400,
            letterSpacing: "-0.02em",
            textAlign: "center",
            marginBottom: 32,
          }}
        >
          The Debt Guy <span style={{ fontStyle: "italic", color: GOLD }}>dashboard</span>
        </h1>

        <label style={{ ...monoStyle, fontSize: 10, color: GOLD, opacity: 0.85, display: "block", marginBottom: 4 }}>
          Password
        </label>
        <input
          data-testid="admin-password-input"
          type="password"
          autoFocus
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={{
            background: "transparent",
            border: "none",
            borderBottom: `1px solid ${CREAM}33`,
            color: CREAM,
            fontFamily: "'Inter', sans-serif",
            fontSize: 16,
            padding: "12px 0",
            width: "100%",
            outline: "none",
            borderRadius: 0,
          }}
        />

        {err && <p data-testid="admin-error" style={{ color: "#ff8080", fontSize: 12, marginTop: 10 }}>{err}</p>}

        <button
          data-testid="admin-login-btn"
          type="submit"
          disabled={busy || !password}
          style={{
            marginTop: 28,
            width: "100%",
            background: GOLD,
            color: BG,
            border: "none",
            padding: "16px 22px",
            ...monoStyle,
            fontSize: 11,
            fontWeight: 500,
            cursor: busy ? "wait" : "pointer",
            opacity: busy || !password ? 0.6 : 1,
          }}
        >
          {busy ? "..." : "Enter"}
        </button>
      </form>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div style={{ padding: "20px 24px", border: `1px solid ${CREAM}14`, background: BG_ALT }}>
      <div style={{ ...monoStyle, fontSize: 9, opacity: 0.5, marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: "'Fraunces', serif", fontSize: 38, fontWeight: 300, letterSpacing: "-0.02em", color: CREAM }}>
        {value}
      </div>
    </div>
  );
}

function Table({ title, rows, columns, kind, onDelete, testid, searchable = true }) {
  const [q, setQ] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const filtered = rows.filter((r) => {
    if (q) {
      const needle = q.toLowerCase();
      const blob = [r.email, r.name, r.company, r.package_name, r.notes]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      if (!blob.includes(needle)) return false;
    }
    if (from || to) {
      if (!r.created_at) return false;
      const t = new Date(r.created_at).getTime();
      if (Number.isNaN(t)) return false;
      if (from && t < new Date(from + "T00:00:00").getTime()) return false;
      if (to && t > new Date(to + "T23:59:59").getTime()) return false;
    }
    return true;
  });

  const exportCSV = () => {
    const headers = columns.map((c) => c.label);
    const escape = (v) => {
      if (v == null) return "";
      const s = typeof v === "object" ? JSON.stringify(v) : String(v);
      if (s.includes('"') || s.includes(",") || s.includes("\n")) {
        return `"${s.replace(/"/g, '""')}"`;
      }
      return s;
    };
    const lines = [headers.join(",")];
    filtered.forEach((r) => {
      lines.push(
        columns
          .map((c) => {
            // Use raw value for CSV (not the JSX render), except for computed fields
            const raw = r[c.key];
            if (c.csv) return escape(c.csv(r));
            return escape(raw);
          })
          .join(",")
      );
    });
    const csv = lines.join("\n");
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const stamp = new Date().toISOString().slice(0, 10);
    a.href = url;
    a.download = `capital-circle-${kind || testid}-${stamp}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const inputStyle = {
    background: "transparent",
    border: `1px solid ${CREAM}22`,
    color: CREAM,
    fontFamily: "'Inter', sans-serif",
    fontSize: 12,
    padding: "8px 10px",
    outline: "none",
    borderRadius: 0,
    colorScheme: "dark",
  };

  return (
    <section data-testid={testid} style={{ marginTop: 40 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 16,
          flexWrap: "wrap",
        }}
      >
        <h2
          style={{
            fontFamily: "'Fraunces', serif",
            fontSize: 26,
            fontWeight: 400,
            letterSpacing: "-0.02em",
          }}
        >
          {title}
        </h2>
        <span style={{ ...monoStyle, fontSize: 10, color: GOLD, opacity: 0.7 }}>
          {filtered.length}
          {filtered.length !== rows.length ? ` / ${rows.length}` : ""}
        </span>

        <div style={{ flex: 1 }} />

        {searchable && (
          <>
            <input
              data-testid={`${testid}-search`}
              placeholder="Search email / name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ ...inputStyle, width: 220 }}
            />
            <input
              data-testid={`${testid}-from`}
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              aria-label="From date"
              style={inputStyle}
            />
            <input
              data-testid={`${testid}-to`}
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              aria-label="To date"
              style={inputStyle}
            />
            {(q || from || to) && (
              <button
                data-testid={`${testid}-clear`}
                onClick={() => {
                  setQ("");
                  setFrom("");
                  setTo("");
                }}
                style={{
                  ...monoStyle,
                  fontSize: 9,
                  background: "transparent",
                  color: CREAM,
                  border: `1px solid ${CREAM}22`,
                  padding: "8px 12px",
                  cursor: "pointer",
                  opacity: 0.7,
                }}
              >
                Clear
              </button>
            )}
          </>
        )}

        <button
          data-testid={`${testid}-export`}
          onClick={exportCSV}
          disabled={filtered.length === 0}
          style={{
            ...monoStyle,
            fontSize: 10,
            background: GOLD,
            color: BG,
            border: "none",
            padding: "9px 14px",
            cursor: filtered.length === 0 ? "not-allowed" : "pointer",
            fontWeight: 500,
            opacity: filtered.length === 0 ? 0.4 : 1,
          }}
        >
          ↓ CSV
        </button>
      </div>

      {filtered.length === 0 ? (
        <div
          style={{
            padding: 28,
            border: `1px dashed ${CREAM}22`,
            opacity: 0.6,
            fontSize: 14,
          }}
        >
          {rows.length === 0 ? "No entries yet." : "No matches for current filter."}
        </div>
      ) : (
        <div style={{ overflowX: "auto", border: `1px solid ${CREAM}14` }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: BG_ALT }}>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    style={{
                      ...monoStyle,
                      fontSize: 9,
                      color: GOLD,
                      textAlign: "left",
                      padding: "12px 14px",
                      borderBottom: `1px solid ${CREAM}14`,
                      fontWeight: 500,
                    }}
                  >
                    {c.label}
                  </th>
                ))}
                {onDelete && <th style={{ padding: "12px 14px", borderBottom: `1px solid ${CREAM}14` }} />}
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id || r.session_id} style={{ borderBottom: `1px solid ${CREAM}10` }}>
                  {columns.map((c) => (
                    <td
                      key={c.key}
                      style={{
                        padding: "14px 14px",
                        color: CREAM,
                        opacity: 0.85,
                        verticalAlign: "top",
                        maxWidth: c.max || "none",
                        wordBreak: c.wrap ? "break-word" : "normal",
                      }}
                    >
                      {c.render ? c.render(r) : r[c.key] || "—"}
                    </td>
                  ))}
                  {onDelete && (
                    <td style={{ padding: "14px 14px", textAlign: "right" }}>
                      <button
                        data-testid={`admin-delete-${kind}-${r.id}`}
                        onClick={() => onDelete(kind, r.id)}
                        style={{
                          background: "transparent",
                          border: `1px solid ${CREAM}22`,
                          color: CREAM,
                          opacity: 0.55,
                          ...monoStyle,
                          fontSize: 9,
                          padding: "6px 10px",
                          cursor: "pointer",
                        }}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function LiveStatsPanel({ token }) {
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/stats/live`)
      .then(({ data }) => setStats(data))
      .catch(() => setStats({}));
  }, []);

  const update = (k, v) => setStats((s) => ({ ...(s || {}), [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        allocators_confirmed: stats.allocators_confirmed === "" || stats.allocators_confirmed == null ? null : Number(stats.allocators_confirmed),
        seats_left: stats.seats_left === "" || stats.seats_left == null ? null : Number(stats.seats_left),
        companies_admitted: stats.companies_admitted === "" || stats.companies_admitted == null ? null : Number(stats.companies_admitted),
        applications_this_week: stats.applications_this_week === "" || stats.applications_this_week == null ? null : Number(stats.applications_this_week),
        custom_message: stats.custom_message || null,
        show_on_promo_bar: !!stats.show_on_promo_bar,
      };
      const { data } = await axios.patch(`${API}/admin/stats/live`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(data.stats || body);
      setSavedAt(new Date());
    } catch {
      window.alert("Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!stats) return null;

  const labelStyle = { ...monoStyle, fontSize: 9, color: GOLD, opacity: 0.8, display: "block", marginBottom: 6 };
  const inputStyle = {
    background: "transparent",
    border: `1px solid ${CREAM}22`,
    color: CREAM,
    fontFamily: "'Inter', sans-serif",
    fontSize: 13,
    padding: "8px 10px",
    outline: "none",
    borderRadius: 0,
    width: "100%",
    colorScheme: "dark",
  };

  return (
    <section data-testid="admin-live-stats" style={{ marginTop: 40, border: `1px solid ${GOLD}33`, background: BG_ALT, padding: "22px 24px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 400, letterSpacing: "-0.02em" }}>
          live promo stats
        </h2>
        <span style={{ ...monoStyle, fontSize: 9, color: GOLD, opacity: 0.7 }}>editable promo bar</span>
        <div style={{ flex: 1 }} />
        {savedAt && (
          <span style={{ ...monoStyle, fontSize: 9, color: "#4ade80", opacity: 0.9 }}>
            saved {savedAt.toLocaleTimeString()}
          </span>
        )}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 16 }}>
        <div>
          <label style={labelStyle}>allocators confirmed</label>
          <input
            data-testid="stats-allocators"
            type="number"
            min={0}
            value={stats.allocators_confirmed ?? ""}
            onChange={(e) => update("allocators_confirmed", e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>seats left</label>
          <input
            data-testid="stats-seats"
            type="number"
            min={0}
            value={stats.seats_left ?? ""}
            onChange={(e) => update("seats_left", e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>companies admitted</label>
          <input
            data-testid="stats-admitted"
            type="number"
            min={0}
            value={stats.companies_admitted ?? ""}
            onChange={(e) => update("companies_admitted", e.target.value)}
            style={inputStyle}
          />
        </div>
        <div>
          <label style={labelStyle}>apps this week</label>
          <input
            data-testid="stats-apps-week"
            type="number"
            min={0}
            value={stats.applications_this_week ?? ""}
            onChange={(e) => update("applications_this_week", e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ gridColumn: "1 / -1" }}>
          <label style={labelStyle}>custom promo message (overrides default)</label>
          <input
            data-testid="stats-message"
            type="text"
            value={stats.custom_message ?? ""}
            onChange={(e) => update("custom_message", e.target.value)}
            style={inputStyle}
            placeholder="e.g. first cohort · Q4 2026 · fully online"
            maxLength={200}
          />
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 18, marginTop: 20 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
          <input
            data-testid="stats-show"
            type="checkbox"
            checked={!!stats.show_on_promo_bar}
            onChange={(e) => update("show_on_promo_bar", e.target.checked)}
            style={{ accentColor: GOLD, cursor: "pointer" }}
          />
          <span style={{ ...monoStyle, fontSize: 10, color: CREAM, opacity: 0.85 }}>show on promo bar</span>
        </label>
        <div style={{ flex: 1 }} />
        <button
          data-testid="stats-save"
          onClick={save}
          disabled={saving}
          style={{
            ...monoStyle,
            fontSize: 10,
            background: GOLD,
            color: BG,
            border: "none",
            padding: "10px 22px",
            fontWeight: 500,
            cursor: saving ? "wait" : "pointer",
            opacity: saving ? 0.7 : 1,
          }}
        >
          {saving ? "saving..." : "save"}
        </button>
      </div>
    </section>
  );
}

function PortfolioPanel({ token }) {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const emptyForm = {
    name: "",
    asset_class: "",
    geography: "",
    cohort_batch: "Q4 2026",
    status: "coming_soon",
    facility_size_usd: "",
    time_to_close_days: "",
    anchor_allocator: "",
    website: "",
    logo_url: "",
    blurb: "",
    featured: false,
    published: true,
  };
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/portfolio`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCompanies(data.companies || []);
    } catch (error) {
      console.error("Failed to load portfolio:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setErr("");
    if (!form.name.trim()) {
      setErr("Company name is required.");
      return;
    }
    try {
      const body = {
        ...form,
        time_to_close_days:
          form.time_to_close_days === "" ? null : Number(form.time_to_close_days),
      };
      await axios.post(`${API}/admin/portfolio`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setForm(emptyForm);
      setShowForm(false);
      await load();
    } catch {
      setErr("Create failed.");
    }
  };

  const del = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this portfolio company?")) return;
    await axios.delete(`${API}/admin/portfolio/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await load();
  };

  const togglePublished = async (c) => {
    await axios.patch(
      `${API}/admin/portfolio/${c.id}`,
      { published: !c.published },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await load();
  };

  const inputStyle = {
    background: "transparent",
    border: `1px solid ${CREAM}22`,
    color: CREAM,
    fontFamily: "'Inter', sans-serif",
    fontSize: 13,
    padding: "8px 10px",
    outline: "none",
    borderRadius: 0,
    width: "100%",
    colorScheme: "dark",
  };
  const labelStyle = { ...monoStyle, fontSize: 9, color: GOLD, opacity: 0.8, display: "block", marginBottom: 6 };

  return (
    <section data-testid="admin-portfolio" style={{ marginTop: 40 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 400, letterSpacing: "-0.02em" }}>
          portfolio companies
        </h2>
        <span style={{ ...monoStyle, fontSize: 10, color: GOLD, opacity: 0.7 }}>{companies.length}</span>
        <div style={{ flex: 1 }} />
        <button
          data-testid="portfolio-add-btn"
          onClick={() => setShowForm((v) => !v)}
          style={{
            ...monoStyle,
            fontSize: 10,
            background: showForm ? "transparent" : GOLD,
            color: showForm ? CREAM : BG,
            border: showForm ? `1px solid ${CREAM}22` : "none",
            padding: "10px 18px",
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          {showForm ? "× close" : "+ add company"}
        </button>
      </div>

      {showForm && (
        <div
          data-testid="portfolio-form"
          style={{ border: `1px solid ${GOLD}33`, background: BG_ALT, padding: 22, marginBottom: 20 }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14 }}>
            <div>
              <label style={labelStyle}>name *</label>
              <input
                data-testid="portfolio-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>asset class</label>
              <input
                value={form.asset_class}
                onChange={(e) => setForm({ ...form, asset_class: e.target.value })}
                style={inputStyle}
                placeholder="SMB invoice factoring"
              />
            </div>
            <div>
              <label style={labelStyle}>geography</label>
              <input
                value={form.geography}
                onChange={(e) => setForm({ ...form, geography: e.target.value })}
                style={inputStyle}
                placeholder="Mexico, LATAM..."
              />
            </div>
            <div>
              <label style={labelStyle}>cohort batch</label>
              <input
                value={form.cohort_batch}
                onChange={(e) => setForm({ ...form, cohort_batch: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                style={inputStyle}
              >
                <option value="coming_soon">coming soon</option>
                <option value="admitted">admitted</option>
                <option value="graduated">graduated</option>
                <option value="closed">facility closed</option>
              </select>
            </div>
            <div>
              <label style={labelStyle}>facility size (USD)</label>
              <input
                value={form.facility_size_usd}
                onChange={(e) => setForm({ ...form, facility_size_usd: e.target.value })}
                style={inputStyle}
                placeholder="$12M"
              />
            </div>
            <div>
              <label style={labelStyle}>time to close (days)</label>
              <input
                type="number"
                value={form.time_to_close_days}
                onChange={(e) => setForm({ ...form, time_to_close_days: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>anchor allocator</label>
              <input
                value={form.anchor_allocator}
                onChange={(e) => setForm({ ...form, anchor_allocator: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>website</label>
              <input
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                style={inputStyle}
                placeholder="https://"
              />
            </div>
            <div>
              <label style={labelStyle}>logo url</label>
              <input
                value={form.logo_url}
                onChange={(e) => setForm({ ...form, logo_url: e.target.value })}
                style={inputStyle}
                placeholder="https://..."
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>blurb (1-2 sentences)</label>
              <textarea
                value={form.blurb}
                onChange={(e) => setForm({ ...form, blurb: e.target.value })}
                style={{ ...inputStyle, resize: "vertical", minHeight: 64, fontFamily: "'Inter', sans-serif" }}
                maxLength={600}
              />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 18, flexWrap: "wrap" }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                style={{ accentColor: GOLD }}
              />
              <span style={{ ...monoStyle, fontSize: 10, color: CREAM, opacity: 0.85 }}>featured</span>
            </label>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                style={{ accentColor: GOLD }}
              />
              <span style={{ ...monoStyle, fontSize: 10, color: CREAM, opacity: 0.85 }}>published (public)</span>
            </label>
            <div style={{ flex: 1 }} />
            {err && <span style={{ color: "#ff8080", fontSize: 11 }}>{err}</span>}
            <button
              data-testid="portfolio-form-save"
              onClick={create}
              style={{
                ...monoStyle,
                fontSize: 10,
                background: GOLD,
                color: BG,
                border: "none",
                padding: "10px 22px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              create
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ opacity: 0.5, padding: 20 }}>loading...</div>
      ) : companies.length === 0 ? (
        <div style={{ padding: 20, border: `1px dashed ${CREAM}22`, opacity: 0.6, fontSize: 13 }}>
          No portfolio companies yet. Click "+ add company" to create the first entry for Cohort 0.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 12 }}>
          {companies.map((c) => (
            <div
              key={c.id}
              data-testid={`admin-portfolio-card-${(c.name || "").toLowerCase().replace(/\s+/g, "-")}`}
              style={{
                background: BG_ALT,
                border: `1px solid ${CREAM}14`,
                padding: "16px 18px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, letterSpacing: "-0.01em" }}>{c.name}</div>
                <span style={{ ...monoStyle, fontSize: 8, padding: "2px 6px", border: `1px solid ${GOLD}44`, color: GOLD }}>
                  {(c.status || "").replace("_", " ")}
                </span>
              </div>
              <div style={{ fontSize: 12, opacity: 0.7 }}>
                {c.asset_class || "—"} · {c.geography || "—"}
              </div>
              <div style={{ fontSize: 10.5, opacity: 0.55, ...monoStyle }}>{c.cohort_batch}</div>
              {c.facility_size_usd && (
                <div style={{ fontSize: 13, color: GOLD }}>
                  {c.facility_size_usd}
                  {c.time_to_close_days ? ` · ${c.time_to_close_days} days` : ""}
                </div>
              )}
              <div style={{ display: "flex", gap: 6, marginTop: "auto", paddingTop: 10, borderTop: `1px solid ${CREAM}0e` }}>
                <button
                  onClick={() => togglePublished(c)}
                  style={{
                    ...monoStyle,
                    fontSize: 8.5,
                    background: "transparent",
                    border: `1px solid ${c.published ? "#4ade80" : CREAM + "22"}`,
                    color: c.published ? "#4ade80" : CREAM,
                    padding: "5px 9px",
                    cursor: "pointer",
                  }}
                >
                  {c.published ? "● public" : "○ hidden"}
                </button>
                <div style={{ flex: 1 }} />
                <button
                  onClick={() => del(c.id)}
                  style={{
                    ...monoStyle,
                    fontSize: 8.5,
                    background: "transparent",
                    border: `1px solid #ff808044`,
                    color: "#ff8080",
                    padding: "5px 9px",
                    cursor: "pointer",
                  }}
                >
                  delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function AdminAnalystPanel({ token, applications, selectedAppId, setSelectedAppId }) {
  const genMsgId = () => "m-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
  const [messages, setMessages] = useState([
    { id: genMsgId(), role: "assistant", content: "admin analyst ready. select an applicant above or ask a general question about the pipeline." },
  ]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId] = useState(() => "admin-" + Math.random().toString(36).slice(2) + Date.now().toString(36));
  const scrollRef = React.useRef(null);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const selected = applications.find((a) => a.id === selectedAppId);

  const send = async (textOverride) => {
    const text = (textOverride ?? input).trim();
    if (!text || sending) return;
    setMessages((m) => [...m, { id: genMsgId(), role: "user", content: text }]);
    setInput("");
    setSending(true);

    const payload = { session_id: sessionId, message: text, application_id: selectedAppId || null };
    const cfg = { headers: { Authorization: `Bearer ${token}` } };

    let attempt = 0;
    while (attempt < 2) {
      try {
        const { data } = await axios.post(`${API}/admin/chat`, payload, cfg);
        setMessages((m) => [...m, { id: genMsgId(), role: "assistant", content: data.reply }]);
        setSending(false);
        return;
      } catch (e) {
        const status = e.response?.status;
        if (status === 401) {
          setMessages((m) => [...m, { id: genMsgId(), role: "assistant", content: "session expired. please re-login." }]);
          setSending(false);
          return;
        }
        if (status === 502 && attempt === 0) {
          await new Promise((r) => setTimeout(r, 800));
          attempt += 1;
          continue;
        }
        setMessages((m) => [...m, { id: genMsgId(), role: "assistant", content: "error reaching analyst. try again in a moment." }]);
        setSending(false);
        return;
      }
    }
  };

  const QUICK_ACTIONS = selected
    ? ["score this founder", "due diligence questions", "which sherpa should pair?", "red flags?"]
    : ["summarize the pipeline", "how many are strong fits?", "average raise size?"];

  return (
    <section
      data-testid="admin-analyst"
      style={{
        marginTop: 40,
        border: `1px solid ${GOLD}44`,
        background: `linear-gradient(180deg, ${BG_ALT}, ${BG})`,
        padding: "22px 24px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
        <div
          style={{
            width: 30,
            height: 30,
            borderRadius: "50%",
            background: `linear-gradient(135deg, ${GOLD}, #a8875a)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: BG,
            ...monoStyle,
            fontWeight: 600,
            fontSize: 10,
            letterSpacing: "-0.02em",
          }}
        >
          AI
        </div>
        <div>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 22, fontWeight: 400, letterSpacing: "-0.02em" }}>
            admin analyst
          </h2>
          <div style={{ ...monoStyle, fontSize: 8.5, color: GOLD, opacity: 0.8 }}>
            claude sonnet 4.5 · credit-committee mode
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <select
          data-testid="analyst-applicant-select"
          value={selectedAppId || ""}
          onChange={(e) => setSelectedAppId(e.target.value || null)}
          style={{
            background: BG_ALT,
            color: CREAM,
            border: `1px solid ${GOLD}44`,
            padding: "8px 12px",
            fontSize: 12,
            fontFamily: "'Inter', sans-serif",
            outline: "none",
            minWidth: 240,
            colorScheme: "dark",
          }}
        >
          <option value="">— no applicant loaded —</option>
          {applications.map((a) => (
            <option key={a.id} value={a.id}>
              {a.founder_name} · {a.company_name}
            </option>
          ))}
        </select>
      </div>

      {selected && (
        <div
          data-testid="analyst-applicant-summary"
          style={{
            background: BG_ALT,
            border: `1px solid ${GOLD}22`,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 12,
            color: CREAM,
            opacity: 0.9,
          }}
        >
          <span style={{ ...monoStyle, fontSize: 8.5, color: GOLD, marginRight: 8 }}>CONTEXT LOADED</span>
          {selected.founder_name} · {selected.company_name} · {selected.country || "—"} · {selected.raise_size || "—"} · {selected.raise_type || "—"}
        </div>
      )}

      <div
        ref={scrollRef}
        style={{
          height: 280,
          overflowY: "auto",
          padding: "14px 0",
          display: "flex",
          flexDirection: "column",
          gap: 10,
        }}
      >
        {messages.map((m) => (
          <div
            key={m.id}
            data-testid={`analyst-msg-${m.role}`}
            style={{
              alignSelf: m.role === "user" ? "flex-end" : "flex-start",
              maxWidth: "86%",
              background: m.role === "user" ? GOLD : BG_ALT,
              color: m.role === "user" ? BG : CREAM,
              border: m.role === "user" ? "none" : `1px solid ${CREAM}14`,
              padding: "10px 14px",
              fontSize: 13,
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {m.content}
          </div>
        ))}
        {sending && (
          <div style={{ alignSelf: "flex-start", ...monoStyle, fontSize: 10, color: GOLD, opacity: 0.7 }}>
            analyzing...
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        {QUICK_ACTIONS.map((q) => (
          <button
            key={q}
            data-testid={`analyst-quick-${q.replace(/\s+/g, "-").replace(/\?/g, "")}`}
            onClick={() => send(q)}
            disabled={sending}
            style={{
              ...monoStyle,
              fontSize: 9,
              background: "transparent",
              color: CREAM,
              border: `1px solid ${GOLD}33`,
              padding: "7px 11px",
              cursor: sending ? "wait" : "pointer",
              opacity: sending ? 0.5 : 0.9,
            }}
          >
            → {q}
          </button>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <input
          data-testid="analyst-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              send();
            }
          }}
          disabled={sending}
          placeholder={selected ? `ask about ${selected.founder_name}...` : "ask the analyst..."}
          style={{
            flex: 1,
            background: BG_ALT,
            border: `1px solid ${CREAM}22`,
            color: CREAM,
            padding: "11px 14px",
            fontSize: 13,
            fontFamily: "'Inter', sans-serif",
            outline: "none",
          }}
        />
        <button
          data-testid="analyst-send"
          onClick={() => send()}
          disabled={sending || !input.trim()}
          style={{
            ...monoStyle,
            fontSize: 10,
            background: input.trim() && !sending ? GOLD : `${GOLD}44`,
            color: BG,
            border: "none",
            padding: "0 18px",
            cursor: input.trim() && !sending ? "pointer" : "not-allowed",
            fontWeight: 500,
          }}
        >
          send
        </button>
      </div>
    </section>
  );
}


function NetworkPanel({ token }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [latestInvite, setLatestInvite] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const empty = {
    name: "",
    email: "",
    role: "",
    company: "",
    asset_class: "",
    geography: "",
    bio: "",
    looking_for: "",
    photo_url: "",
    cohort: "Q4 2026",
  };
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/admin/network/members`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMembers(data.members || []);
    } catch (error) {
      console.error("Failed to load network members:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const inviteUrl = (path) => `${window.location.origin}${path}`;

  const create = async () => {
    setErr("");
    if (!form.name.trim() || !form.email.trim()) {
      setErr("Name and email are required.");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/admin/network/members`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLatestInvite({
        member: data.member,
        url: inviteUrl(data.invite_url_path),
        days: data.expires_in_days,
      });
      setForm(empty);
      setShowForm(false);
      await load();
    } catch (error) {
      console.error("Create member failed:", error);
      setErr("Create failed.");
    }
  };

  const regenInvite = async (memberId) => {
    try {
      const { data } = await axios.post(
        `${API}/admin/network/members/${memberId}/invite`,
        null,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const member = members.find((m) => m.id === memberId);
      setLatestInvite({
        member,
        url: inviteUrl(data.invite_url_path),
        days: data.expires_in_days,
      });
    } catch (error) {
      console.error("Regen invite failed:", error);
    }
  };

  const toggleActive = async (m) => {
    await axios.patch(
      `${API}/admin/network/members/${m.id}`,
      { is_active: !m.is_active },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    await load();
  };

  const del = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Remove this member and all their invites/sessions?")) return;
    await axios.delete(`${API}/admin/network/members/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    await load();
  };

  const copy = (id, text) => {
    navigator.clipboard?.writeText(text);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 1800);
  };

  const inputStyle = {
    background: "transparent",
    border: `1px solid ${CREAM}22`,
    color: CREAM,
    fontFamily: "'Inter', sans-serif",
    fontSize: 13,
    padding: "8px 10px",
    outline: "none",
    borderRadius: 0,
    width: "100%",
    colorScheme: "dark",
  };
  const labelStyle = { ...monoStyle, fontSize: 9, color: GOLD, opacity: 0.8, display: "block", marginBottom: 6 };

  return (
    <section data-testid="admin-network" style={{ marginTop: 40 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: 26, fontWeight: 400, letterSpacing: "-0.02em" }}>
          alumni bookface
        </h2>
        <span style={{ ...monoStyle, fontSize: 10, color: GOLD, opacity: 0.7 }}>{members.length} members</span>
        <div style={{ flex: 1 }} />
        <a
          href="/network"
          target="_blank"
          rel="noreferrer"
          style={{
            ...monoStyle,
            fontSize: 9.5,
            color: CREAM,
            opacity: 0.65,
            textDecoration: "none",
            border: `1px solid ${CREAM}1f`,
            padding: "8px 12px",
          }}
        >
          open /network →
        </a>
        <button
          data-testid="network-add-btn"
          onClick={() => setShowForm((v) => !v)}
          style={{
            ...monoStyle,
            fontSize: 10,
            background: showForm ? "transparent" : GOLD,
            color: showForm ? CREAM : BG,
            border: showForm ? `1px solid ${CREAM}22` : "none",
            padding: "10px 18px",
            cursor: "pointer",
            fontWeight: 500,
          }}
        >
          {showForm ? "× close" : "+ invite member"}
        </button>
      </div>

      {latestInvite && (
        <div
          data-testid="latest-invite-banner"
          style={{
            border: `1px solid ${GOLD}66`,
            background: `${GOLD}0d`,
            padding: "16px 20px",
            marginBottom: 18,
            display: "flex",
            alignItems: "center",
            gap: 14,
            flexWrap: "wrap",
          }}
        >
          <div style={{ ...monoStyle, fontSize: 9, color: GOLD }}>NEW INVITE</div>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{latestInvite.member?.name}</div>
            <a
              href={latestInvite.url}
              target="_blank"
              rel="noreferrer"
              style={{
                display: "block",
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 11,
                opacity: 0.85,
                wordBreak: "break-all",
                marginTop: 4,
                color: GOLD,
                textDecoration: "none",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.textDecoration = "underline")}
              onMouseLeave={(e) => (e.currentTarget.style.textDecoration = "none")}
            >
              {latestInvite.url}
            </a>
            <div style={{ ...monoStyle, fontSize: 8.5, opacity: 0.55, marginTop: 4 }}>
              expires in {latestInvite.days} days · single-use
            </div>
          </div>
          <button
            onClick={() => copy("latest", latestInvite.url)}
            data-testid="copy-latest-invite"
            style={{
              ...monoStyle,
              fontSize: 10,
              background: GOLD,
              color: BG,
              border: "none",
              padding: "10px 18px",
              cursor: "pointer",
              fontWeight: 500,
            }}
          >
            {copiedId === "latest" ? "✓ copied" : "copy link"}
          </button>
          <button
            onClick={() => setLatestInvite(null)}
            style={{
              ...monoStyle,
              fontSize: 10,
              background: "transparent",
              color: CREAM,
              border: `1px solid ${CREAM}22`,
              padding: "10px 14px",
              cursor: "pointer",
              opacity: 0.6,
            }}
          >
            dismiss
          </button>
        </div>
      )}

      {showForm && (
        <div
          data-testid="network-form"
          style={{ border: `1px solid ${GOLD}33`, background: BG_ALT, padding: 22, marginBottom: 20 }}
        >
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14 }}>
            <div>
              <label style={labelStyle}>name *</label>
              <input
                data-testid="network-form-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>email *</label>
              <input
                data-testid="network-form-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                style={inputStyle}
                placeholder="founder@company.com"
              />
            </div>
            <div>
              <label style={labelStyle}>role</label>
              <input
                value={form.role}
                onChange={(e) => setForm({ ...form, role: e.target.value })}
                style={inputStyle}
                placeholder="CEO / CFO / Sherpa"
              />
            </div>
            <div>
              <label style={labelStyle}>company</label>
              <input
                value={form.company}
                onChange={(e) => setForm({ ...form, company: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>cohort</label>
              <input
                value={form.cohort}
                onChange={(e) => setForm({ ...form, cohort: e.target.value })}
                style={inputStyle}
                placeholder="Q4 2026 / Sherpa / The Debt Guy"
              />
            </div>
            <div>
              <label style={labelStyle}>asset class</label>
              <input
                value={form.asset_class}
                onChange={(e) => setForm({ ...form, asset_class: e.target.value })}
                style={inputStyle}
                placeholder="SMB factoring"
              />
            </div>
            <div>
              <label style={labelStyle}>geography</label>
              <input
                value={form.geography}
                onChange={(e) => setForm({ ...form, geography: e.target.value })}
                style={inputStyle}
              />
            </div>
            <div>
              <label style={labelStyle}>photo url</label>
              <input
                value={form.photo_url}
                onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                style={inputStyle}
                placeholder="https://..."
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>looking for</label>
              <input
                value={form.looking_for}
                onChange={(e) => setForm({ ...form, looking_for: e.target.value })}
                style={inputStyle}
                placeholder="warehouse for SMB factoring · LATAM"
              />
            </div>
            <div style={{ gridColumn: "1 / -1" }}>
              <label style={labelStyle}>bio</label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                style={{ ...inputStyle, resize: "vertical", minHeight: 60, fontFamily: "'Inter', sans-serif" }}
                maxLength={600}
              />
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 18, flexWrap: "wrap" }}>
            {err && <span style={{ color: "#ff8080", fontSize: 11 }}>{err}</span>}
            <div style={{ flex: 1 }} />
            <button
              data-testid="network-form-save"
              onClick={create}
              style={{
                ...monoStyle,
                fontSize: 10,
                background: GOLD,
                color: BG,
                border: "none",
                padding: "10px 22px",
                fontWeight: 500,
                cursor: "pointer",
              }}
            >
              create + generate invite
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div style={{ opacity: 0.5, padding: 20 }}>loading…</div>
      ) : members.length === 0 ? (
        <div style={{ padding: 20, border: `1px dashed ${CREAM}22`, opacity: 0.6, fontSize: 13 }}>
          No members yet. Click "+ invite member" — typical seed list: 9 sherpas + the The Debt Guy admins + any pre-cohort 0
          founders you want in the room early.
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {members.map((m) => (
            <div
              key={m.id}
              data-testid={`admin-network-card-${m.id}`}
              style={{
                background: BG_ALT,
                border: `1px solid ${CREAM}14`,
                padding: "16px 18px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    fontFamily: "'Fraunces', serif",
                    fontSize: 17,
                    letterSpacing: "-0.01em",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {m.name}
                </div>
                <span
                  style={{
                    ...monoStyle,
                    fontSize: 7.5,
                    padding: "2px 5px",
                    border: `1px solid ${m.is_active ? "#4ade80" : CREAM + "22"}`,
                    color: m.is_active ? "#4ade80" : CREAM,
                  }}
                >
                  {m.is_active ? "ACTIVE" : "INACTIVE"}
                </span>
              </div>
              <div style={{ fontSize: 11.5, opacity: 0.65 }}>
                {m.role || "—"} · {m.company || "—"}
              </div>
              <div style={{ ...monoStyle, fontSize: 9, color: GOLD, opacity: 0.8 }}>
                {m.cohort || "MEMBER"}
              </div>
              <div style={{ fontSize: 11, opacity: 0.5, fontFamily: "'JetBrains Mono', monospace" }}>{m.email}</div>
              <div
                style={{
                  display: "flex",
                  gap: 6,
                  marginTop: "auto",
                  paddingTop: 10,
                  borderTop: `1px solid ${CREAM}0e`,
                  flexWrap: "wrap",
                }}
              >
                <button
                  onClick={() => regenInvite(m.id)}
                  data-testid={`regen-invite-${m.id}`}
                  style={{
                    ...monoStyle,
                    fontSize: 8.5,
                    background: "transparent",
                    border: `1px solid ${GOLD}55`,
                    color: GOLD,
                    padding: "5px 9px",
                    cursor: "pointer",
                  }}
                >
                  + invite link
                </button>
                <button
                  onClick={() => toggleActive(m)}
                  style={{
                    ...monoStyle,
                    fontSize: 8.5,
                    background: "transparent",
                    border: `1px solid ${CREAM}22`,
                    color: CREAM,
                    padding: "5px 9px",
                    cursor: "pointer",
                    opacity: 0.7,
                  }}
                >
                  {m.is_active ? "deactivate" : "activate"}
                </button>
                <div style={{ flex: 1 }} />
                <button
                  onClick={() => del(m.id)}
                  style={{
                    ...monoStyle,
                    fontSize: 8.5,
                    background: "transparent",
                    border: `1px solid #ff808044`,
                    color: "#ff8080",
                    padding: "5px 9px",
                    cursor: "pointer",
                  }}
                >
                  delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}


function DashboardView({ token, onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [selectedAppId, setSelectedAppId] = useState(null);

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setData(data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
      else setErr("Failed to load.");
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const deleteLead = async (kind, id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await axios.delete(`${API}/admin/lead/${kind}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
    } catch {
      setErr("Delete failed.");
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/admin/logout`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      console.warn("Logout API call failed (clearing local session anyway):", error);
    }
    onLogout();
  };

  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  return (
    <div
      data-testid="admin-dashboard"
      style={{ minHeight: "100vh", background: BG, color: CREAM, fontFamily: "'Inter', sans-serif" }}
    >
      <style>{`${fontImport} body { background: ${BG}; margin: 0; }`}</style>

      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "22px 32px",
          borderBottom: `1px solid ${CREAM}14`,
          position: "sticky",
          top: 0,
          background: "rgba(14,13,11,0.95)",
          backdropFilter: "blur(10px)",
          zIndex: 10,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LogoMark mode="dark" size={34} />
          <div>
            <div style={{ ...monoStyle, fontSize: 9, opacity: 0.5 }}>The Debt Guy</div>
            <div style={{ fontFamily: "'Fraunces', serif", fontSize: 18, letterSpacing: "-0.02em" }}>Admin</div>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <button
            data-testid="admin-refresh-btn"
            onClick={load}
            style={{ ...monoStyle, fontSize: 10, background: "transparent", color: CREAM, border: `1px solid ${CREAM}22`, padding: "10px 16px", cursor: "pointer" }}
          >
            Refresh
          </button>
          <button
            data-testid="admin-logout-btn"
            onClick={logout}
            style={{ ...monoStyle, fontSize: 10, background: GOLD, color: BG, border: "none", padding: "10px 16px", cursor: "pointer", fontWeight: 500 }}
          >
            Logout
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 1280, margin: "0 auto", padding: "40px 32px 80px" }}>
        {loading ? (
          <div style={{ opacity: 0.6, padding: 40, textAlign: "center" }} data-testid="admin-loading">Loading…</div>
        ) : err ? (
          <div style={{ color: "#ff8080", padding: 20 }}>{err}</div>
        ) : (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 14,
                marginBottom: 20,
              }}
            >
              <Stat label="Fintech apps" value={data.counts.applications || 0} />
              <Stat label="Allocator apps" value={data.counts.allocator_applications || 0} />
              <Stat label="SMB apps" value={data.counts.smb_applications || 0} />
              <Stat label="Course waitlist" value={data.counts.course} />
              <Stat label="Mandate leads" value={data.counts.mandate} />
              <Stat label="Paid" value={data.counts.paid} />
            </div>

            <LiveStatsPanel token={token} />

            <AdminAnalystPanel
              token={token}
              applications={data.applications || []}
              selectedAppId={selectedAppId}
              setSelectedAppId={setSelectedAppId}
            />

            <PortfolioPanel token={token} />

            <NetworkPanel token={token} />

            <Table
              testid="admin-table-applications"
              title="The Debt Guy applications"
              kind="application"
              rows={data.applications || []}
              onDelete={async (_kind, id) => {
                if (!window.confirm("Delete this application permanently?")) return;
                try {
                  await axios.delete(`${API}/admin/application/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  await load();
                } catch {
                  setErr("Delete failed.");
                }
              }}
              columns={[
                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                {
                  key: "status",
                  label: "Status",
                  render: (r) => {
                    const s = r.status || "new";
                    const color = s === "admitted" ? GOLD : s === "rejected" ? "#ff8080" : s === "interviewing" ? "#4ac1a7" : CREAM;
                    return (
                      <span
                        style={{
                          ...monoStyle,
                          fontSize: 9,
                          padding: "3px 8px",
                          border: `1px solid ${color}55`,
                          color,
                        }}
                      >
                        {s}
                      </span>
                    );
                  },
                },
                { key: "founder_name", label: "Founder" },
                { key: "founder_email", label: "Email" },
                { key: "company_name", label: "Company" },
                { key: "country", label: "Country" },
                { key: "product", label: "Product", max: 220, wrap: true },
                { key: "stage", label: "Stage" },
                { key: "active_book", label: "Book" },
                { key: "raise_size", label: "Raise" },
                { key: "raise_type", label: "Instrument" },
                { key: "timeline", label: "Timeline" },
                { key: "format", label: "Format" },
                { key: "why_now", label: "Why now", max: 320, wrap: true },
                { key: "batch", label: "Batch" },
              ]}
            />

            <Table
              testid="admin-table-allocator"
              title="Track 02 · Allocator applications (institutional)"
              kind="allocator"
              rows={data.allocator_applications || []}
              onDelete={async (_k, id) => {
                if (!window.confirm("Delete this allocator application?")) return;
                try {
                  await axios.delete(`${API}/admin/allocator/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  await load();
                } catch {
                  setErr("Delete failed.");
                }
              }}
              columns={[
                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                { key: "contact_name", label: "Contact" },
                { key: "contact_email", label: "Email" },
                { key: "firm_name", label: "Firm" },
                { key: "role", label: "Role" },
                { key: "aum_usd", label: "AUM" },
                { key: "ticket_min", label: "Min $" },
                { key: "ticket_max", label: "Max $" },
                { key: "geographies", label: "Geo" },
                { key: "asset_classes", label: "Asset classes", max: 260, wrap: true, render: (r) => (r.asset_classes || []).join(", ") },
                { key: "instruments", label: "Instruments", max: 220, wrap: true, render: (r) => (r.instruments || []).join(", ") },
                { key: "notes", label: "Notes", max: 320, wrap: true },
              ]}
            />

            <Table
              testid="admin-table-smb"
              title="Track 03 · SMB applications (LATAM)"
              kind="smb"
              rows={data.smb_applications || []}
              onDelete={async (_k, id) => {
                if (!window.confirm("Delete this SMB application?")) return;
                try {
                  await axios.delete(`${API}/admin/smb/${id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  });
                  await load();
                } catch {
                  setErr("Delete failed.");
                }
              }}
              columns={[
                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                { key: "lang", label: "Lang" },
                { key: "founder_name", label: "Founder" },
                { key: "founder_email", label: "Email" },
                { key: "phone", label: "Phone" },
                { key: "company_name", label: "Company" },
                { key: "rfc", label: "RFC" },
                { key: "state", label: "State" },
                { key: "industry", label: "Industry" },
                { key: "years_operating", label: "Years" },
                { key: "annual_revenue", label: "Revenue" },
                { key: "raise_size", label: "Raise" },
                { key: "use_of_funds", label: "Use of funds", max: 240, wrap: true },
                { key: "current_lenders", label: "Current lenders", max: 220, wrap: true },
                { key: "why_now", label: "Why now", max: 320, wrap: true },
              ]}
            />

            <Table
              testid="admin-table-mandate"
              title="Mandate leads (legacy)"
              kind="mandate"
              rows={data.mandate}
              onDelete={deleteLead}
              columns={[
                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "company", label: "Company" },
                { key: "raise_size", label: "Raise" },
                { key: "timeline", label: "Timeline" },
                { key: "notes", label: "Notes", max: 320, wrap: true, render: (r) => r.notes || "—" },
                { key: "lang", label: "Lang" },
              ]}
            />

            <Table
              testid="admin-table-course"
              title="Course waitlist (legacy)"
              kind="course"
              rows={data.course}
              onDelete={deleteLead}
              columns={[
                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                { key: "name", label: "Name" },
                { key: "email", label: "Email" },
                { key: "company", label: "Company" },
                { key: "notes", label: "Notes", max: 320, wrap: true, render: (r) => r.notes || "—" },
                { key: "lang", label: "Lang" },
              ]}
            />

            <Table
              testid="admin-table-payments"
              title="Payments (legacy)"
              kind="payments"
              rows={data.payments}
              columns={[
                { key: "created_at", label: "Date", render: (r) => fmtDate(r.created_at) },
                { key: "package_name", label: "Package" },
                {
                  key: "amount",
                  label: "Amount",
                  render: (r) => `$${Number(r.amount || 0).toFixed(2)} ${(r.currency || "usd").toUpperCase()}`,
                },
                {
                  key: "payment_status",
                  label: "Status",
                  render: (r) => (
                    <span
                      style={{
                        ...monoStyle,
                        fontSize: 9,
                        padding: "3px 8px",
                        border: `1px solid ${r.payment_status === "paid" ? GOLD : CREAM + "33"}`,
                        color: r.payment_status === "paid" ? GOLD : CREAM,
                      }}
                    >
                      {r.payment_status || "—"}
                    </span>
                  ),
                },
                { key: "email", label: "Email" },
                { key: "session_id", label: "Session", max: 180, wrap: true, render: (r) => (r.session_id ? r.session_id.slice(0, 18) + "…" : "—") },
              ]}
            />
          </>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const navigate = useNavigate();
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));
  const [checking, setChecking] = useState(!!token);

  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(() => setChecking(false))
      .catch(() => {
        localStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setChecking(false);
      });
  }, [token]);

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
    navigate("/admin", { replace: true });
  };

  if (checking) {
    return (
      <div style={{ minHeight: "100vh", background: BG, color: CREAM, display: "flex", alignItems: "center", justifyContent: "center" }}>
        <style>{`body { background: ${BG}; margin: 0; }`}</style>
      </div>
    );
  }

  return token ? <DashboardView token={token} onLogout={logout} /> : <LoginView onAuthed={setToken} />;
}
